import {batch} from 'react-redux';
import {FmlxCheckBox} from 'fmlx-common-ui';
import {getIsTrayAttached} from 'stores/selectors/trayCalibrationSelectors';
import {setInstrumentStatus} from 'stores/actions/instrumentAction';
import FormattedHTMLMessage from '../../../components/FormattedHTMLMessage';
import {InstrumentStatusType, RuntimeMode, TrayType, ViewMode} from '../../../constants/dataEnum';
import {changeView, hideLoading, showLoading} from '../../actions/appStateAction';
import {hideModal, setModalContent, setModalInvalid, setModalValid, showModal} from '../../actions/modalStateAction';
import {
  resetWizardState,
  setWizardAllowedNext,
  clearWizardFlag,
  setCurrentStepData,
  setWizardStatus,
  setWizardInitialTrayType,
} from '../../actions/wizardStateAction';
import {resolveIntl} from '../commonBusiness';
import {convertCardStateToCsv} from '../protocolStateConversionBusiness';
import {
  calibrateScanSlantedTips,
  calibrateScanStuckTips,
  calibrateScanTips,
  isA1OffsetCalibrated,
  postMoveCalibrateScanStuckTips,
} from '../instrumentBusiness';
import {generateSetupWizardProtocol} from '../taskListBusinessAction';
import {executeProtocol} from '../runtimeBusiness';
// eslint-disable-next-line import/no-cycle
import {fastCommonActuatorAPIErrorHandler} from '../errorHandlerBusiness';
import {saveState} from '../stateBusiness';
import {setRuntimeProtocolId, setRuntimeProtocolName, setRuntimeStatus} from '../../actions/runtimeStateAction';
import {setOpenedProtocolCheckpoint} from '../../actions/protocolAction';
import {SetupWizardStep, VisionCalibrationSubstep, WizardFlag, WizardStatus} from '../../../constants/SetupWizardEnum';
import {
  getSetupWizardCurrentStepData,
  getSetupWizardFlagMessage,
  incrementSetupWizardSubstep,
  setStepDataByName,
} from './commonSetupWizardBusiness';
import {
  checkIsInstrumentIdleAndShowModal,
  handleDefaultWizardAPICallResponse,
  handleEjectTray,
  handleMoveToIdlePosition,
  handleTouchProbeRetract,
} from './setupWizardInstrumentBusiness';
import {fastRecovery} from '../recoveryBusiness';
import {resetPlateTypeEditorState} from '../../actions/plateTypeEditorStateAction';
import {handleIncrementCurrentWizardView} from './setupWizardNavigationBusiness';
import {isV2Instrument} from '../instrumentStatusBusiness';
import {showLowVolumeTraySetupRequired} from './setupWizardModalBusiness';
import {showMissingTrayModal} from '../trayModalBusiness';

const intl = resolveIntl();

export const initiateWizard = () => async (dispatch, getState) => {
  const isTrayAttached = getIsTrayAttached(getState());
  const {trayCalibrationState} = getState();
  const {trayType} = trayCalibrationState;

  const isA1Calibrated = await dispatch(isA1OffsetCalibrated());

  if (!isA1Calibrated && trayType === TrayType.HIGH_VOLUME_CADDY) {
    dispatch(showLowVolumeTraySetupRequired());
    return;
  }

  if (!isTrayAttached) {
    dispatch(showMissingTrayModal());
    return;
  }

  batch(async () => {
    await dispatch(setWizardInitialTrayType(trayType));
    await dispatch(handleIncrementCurrentWizardView());
    dispatch(setWizardStatus(WizardStatus.ONGOING));

    await dispatch(saveState());
  });
};

export const executeSetupWizardProtocol = () => async (dispatch, getState) => {
  const {sessionState} = getState();
  const {user} = sessionState;

  const loadingMsg = dispatch(getSetupWizardFlagMessage(WizardFlag.LOADING));
  dispatch(showLoading(loadingMsg));

  const isInstrumentIdle = await dispatch(checkIsInstrumentIdleAndShowModal());
  if (!isInstrumentIdle) return;

  const protocol = {id: '00000000-0000-0000-0000-000000000001'};

  await batch(async () => {
    await dispatch(
      generateSetupWizardProtocol((cardState, labwareState) => {
        protocol.csvContent = dispatch(convertCardStateToCsv(undefined, TrayType.NORMAL_CADDY));
        protocol.user = user;
        protocol.stateContent = JSON.stringify({
          cardState,
          labwareState,
        });
      }),
    );
  });

  dispatch(setRuntimeProtocolId(protocol.id));
  dispatch(setRuntimeProtocolName(protocol.name));
  await dispatch(setRuntimeStatus(RuntimeMode.RUNNING));
  await dispatch(setInstrumentStatus(InstrumentStatusType.RUNNING));
  await dispatch(executeProtocol(protocol));

  dispatch(showLoading(loadingMsg));

  await dispatch(saveState());
  await dispatch(setOpenedProtocolCheckpoint(null));
};

export const onExitWizard = () => async (dispatch) => {
  dispatch(clearWizardFlag());
  dispatch(hideModal());
  dispatch(resetWizardState());
  dispatch(resetPlateTypeEditorState());
  dispatch(changeView(ViewMode.EXPLORER));
  dispatch(saveState());
};

export const handleExitWizard = () => async (dispatch, getState) => {
  const {isTrayAttached} = getState().trayCalibrationState;

  const isV2 = isV2Instrument();
  const stepData = dispatch(getSetupWizardCurrentStepData());
  const {stepName, currentSubstep} = stepData || {};

  if (
    !isV2 &&
    stepName === SetupWizardStep.VISION_CALIBRATION &&
    (currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE ||
      currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_RESULT)
  ) {
    dispatch(showHeadCoverConfirmationModal(true, () => dispatch(onExitWizard())));
    return;
  }

  let retractSuccess = true;

  if (isTrayAttached) {
    retractSuccess = await dispatch(handleTouchProbeRetract());
  }

  if (retractSuccess) {
    dispatch(fastRecovery(() => dispatch(onExitWizard())));
  }
};

export const onCalibrateVision = (currentSubstep) => async (dispatch) => {
  dispatch(showLoading());
  let result = '';

  const isInstrumentIdle = await dispatch(checkIsInstrumentIdleAndShowModal());
  if (!isInstrumentIdle) return;

  switch (currentSubstep) {
    case 1:
      result = await dispatch(calibrateScanTips());
      break;
    case 2:
      result = await dispatch(calibrateScanSlantedTips());
      break;
    case 3:
      result = await dispatch(calibrateScanStuckTips());

      if (!isV2Instrument()) {
        await dispatch(postMoveCalibrateScanStuckTips());
      }

      dispatch(incrementSetupWizardSubstep());
      break;
    default:
      break;
  }

  const noErrorAction = async () => {
    dispatch(setCurrentStepData({scanSuccess: true, hasScanned: true}));
    dispatch(setWizardAllowedNext(true));
    await dispatch(saveState());
  };

  const errorAction = () => {
    dispatch(setWizardAllowedNext(false));
  };

  const stepData = dispatch(getSetupWizardCurrentStepData()) || {};
  const {isSlantedDetectionSkipped} = stepData;
  const subStepProcessed = stepData.subStepProcessed ?? [];
  const isStepProcessed = isSlantedDetectionSkipped ? subStepProcessed.length >= 1 : subStepProcessed.length >= 2;

  const handlerOption = {isStepProcessed, isCallCommonActuatorHandler: false, isSetContinueButtonAccess: false};
  const isSuccess = await dispatch(handleDefaultWizardAPICallResponse(result, handlerOption));

  await dispatch(hideLoading());

  if (!isSuccess) {
    dispatch(
      setCurrentStepData({
        scanSuccess: false,
        hasScanned: true,
        subStepProcessed: subStepProcessed.filter((e) => e !== currentSubstep),
      }),
    );

    await dispatch(
      fastCommonActuatorAPIErrorHandler(
        result,
        async () => dispatch(onCalibrateVision(currentSubstep)),
        () => errorAction(),
      ),
    );
  } else {
    dispatch(
      setCurrentStepData({
        subStepProcessed: [...new Set([...subStepProcessed, currentSubstep])],
      }),
    );
    noErrorAction();
  }
};

export const handleCalibrateVision = (currentSubstep) => async (dispatch) => {
  if (currentSubstep === VisionCalibrationSubstep.STUCK_TIPS_GUIDE && !isV2Instrument()) {
    dispatch(showHeadCoverConfirmationModal(false));
  } else {
    dispatch(onCalibrateVision(currentSubstep));
  }
};

// #region modal
export const showConfirmationAbortModal = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({id: 'modal.setupWizard.abort.title'}),
      <FormattedHTMLMessage id="modal.setupWizard.abort.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
          },
          text: intl.formatMessage({id: 'modal.setupWizard.abort.firstButton'}),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(handleExitWizard());
          },
          text: intl.formatMessage({id: 'modal.setupWizard.abort.secondButton'}),
        },
      ],
      () => dispatch(hideModal()),
    ),
  );
};

export const showHeadCoverConfirmationModal =
  (
    // eslint-disable-next-line default-param-last
    isHeadCoverRemoval = true,
    callback,
  ) =>
  (dispatch, getState) => {
    const {wizardState} = getState();
    const {currentWizardView, wizardViewData} = wizardState;
    const {currentSubstep} = wizardViewData[currentWizardView];

    const getContent = (isChecked) => {
      const onChangeHandler = (e) => {
        dispatch(setModalContent(getContent(e.target.checked)));

        if (e.target.checked) {
          dispatch(setModalValid());
        } else {
          dispatch(setModalInvalid());
        }
      };

      return (
        <div>
          <FormattedHTMLMessage id={`modal.setupWizard.headCoverConfirmation.${isHeadCoverRemoval}.text`} />
          <div style={{margin: '14px auto'}}>
            <FmlxCheckBox
              onChange={onChangeHandler}
              size="sm"
              label={
                <span style={{fontSize: '14px'}}>
                  <FormattedHTMLMessage
                    id={`modal.setupWizard.headCoverConfirmation.${isHeadCoverRemoval}.labelCheckbox`}
                  />
                </span>
              }
              checked={isChecked}
            />
          </div>
        </div>
      );
    };

    const onConfirm = async () => {
      dispatch(showLoading());

      if (isHeadCoverRemoval) {
        await dispatch(handleMoveToIdlePosition(false));
      } else {
        await dispatch(onCalibrateVision(currentSubstep));
      }

      if (callback) await callback();
      dispatch(hideLoading());
    };

    dispatch(setModalInvalid());

    dispatch(
      showModal(
        intl.formatMessage({id: 'modal.setupWizard.headCoverConfirmation.title'}),
        getContent(false),
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: async () => {
              dispatch(hideModal());
              onConfirm();
            },
            text: intl.formatMessage({id: 'modal.setupWizard.headCoverConfirmation.firstButton'}),
          },
        ],
        () => dispatch(hideModal()),
        '',
        false,
      ),
    );
  };

export const showConfirmationSkippedStep = (callback) => (dispatch, getState) => {
  const {wizardState} = getState();
  const {wizardViewData} = wizardState;

  const skippedSteps = Object.values(wizardViewData).filter(
    (data) =>
      data.stepName !== SetupWizardStep.WELCOME_SCREEN &&
      data.stepName !== SetupWizardStep.REPORT &&
      data.stepName !== SetupWizardStep.FINISH_SCREEN &&
      data.isStepProcessed === false,
  );

  if (!skippedSteps.length) {
    callback(true);
    return;
  }

  dispatch(
    showModal(
      intl.formatMessage({id: 'modal.setupWizard.skippedStepConfirmation.title'}),
      intl.formatMessage({id: 'modal.setupWizard.skippedStepConfirmation.text'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: async () => {
            dispatch(hideModal());
            callback(true);
          },
          text: intl.formatMessage({id: 'modal.setupWizard.skippedStepConfirmation.firstButton'}),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: async () => {
            dispatch(hideModal());
            callback(false);
          },
          text: intl.formatMessage({id: 'modal.setupWizard.skippedStepConfirmation.secondButton'}),
        },
      ],
      () => {
        dispatch(hideModal());
        callback(false);
      },
      '',
    ),
  );
};

export const showA1OffsetCalibratedModal =
  (callback = () => {}) =>
  (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({id: 'modal.setupWizard.a1OffsetCalibrated.title'}),
        intl.formatMessage({id: 'modal.setupWizard.a1OffsetCalibrated.text'}),
        [
          {
            style: 'primary',
            handlerClick: async () => {
              dispatch(hideModal());
              await dispatch(handleEjectTray());
              dispatch(setStepDataByName(SetupWizardStep.A1_OFFSET_CALIBRATION, {isStepProcessed: true}));
              callback(true);
            },
            text: intl.formatMessage({id: 'modal.setupWizard.a1OffsetCalibrated.firstButton'}),
          },
          {
            style: 'secondary',
            handlerClick: async () => {
              dispatch(hideModal());
              callback(false);
            },
            text: intl.formatMessage({id: 'modal.setupWizard.a1OffsetCalibrated.secondButton'}),
          },
        ],
      ),
    );
  };
// #end region modal
